import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * CreatorSelect — "Created By" picker for the Lead / Candidate / Company forms.
 *
 * Records default to the person filling the form in, but a team leader or admin
 * often enters one on a colleague's behalf, and the record needs to carry that
 * colleague's name. The options come from /users/assignable, which decides
 * server-side who the caller may act for — a recruiter simply sees themselves.
 *
 * Props: value, onChange(userId), testId
 */
export default function CreatorSelect({ value, onChange, testId = "creator-select" }) {
  const { user } = useAuth();
  const [options, setOptions] = useState([]);

  useEffect(() => {
    let live = true;
    api.get("/users/assignable")
      .then(({ data }) => { if (live) setOptions(Array.isArray(data) ? data : []); })
      .catch(() => { if (live) setOptions([]); });
    return () => { live = false; };
  }, []);

  // Nothing to choose between — don't show a dropdown with a single fixed entry.
  if (options.length <= 1) return null;

  return (
    <div data-testid={`${testId}-wrapper`}>
      <Label className="text-xs">Created By</Label>
      <select
        value={value || user?.id || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 border border-[#E5E7EB] rounded-md px-3 text-sm bg-white"
        data-testid={testId}
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}{o.id === user?.id ? " (me)" : ""}{o.role ? ` — ${o.role.replace(/_/g, " ")}` : ""}
          </option>
        ))}
      </select>
    </div>
  );
}
